/**
 * Account Transfer Modal
 * 
 * Allows business owners to transfer their account to a new owner
 * Used when selling a business or changing ownership
 */

import React, { useState } from 'react';
import { X, AlertTriangle, ArrowRight, Building2, Mail, FileText, Shield } from 'lucide-react';
import { createAccountTransferRequest } from '../services/accountTransferService';
import { useEntityContext } from '../hooks/useEntityContext';

interface AccountTransferModalProps {
  currentUserId: string;
  currentUserName: string;
  currentUserEmail: string;
  companyName: string;
  companyKvK: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AccountTransferModal: React.FC<AccountTransferModalProps> = ({
  currentUserId,
  currentUserName,
  currentUserEmail,
  companyName,
  companyKvK,
  onClose,
  onSuccess
}) => {
  const { entities } = useEntityContext(currentUserId);
  const [step, setStep] = useState<'confirm' | 'details' | 'review' | 'processing'>('confirm');
  const [newOwnerEmail, setNewOwnerEmail] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [notes, setNotes] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // All entities are always transferred
  const allEntityIds = entities.map(e => e.id).filter(Boolean) as string[];

  const handleSubmit = async () => {
    if (!agreedToTerms) {
      alert('⚠️ Je moet akkoord gaan met de voorwaarden om door te gaan.');
      return;
    }

    if (!newOwnerEmail.trim() || !transferReason.trim()) {
      alert('⚠️ Vul alle verplichte velden in.');
      return;
    }

    setProcessing(true);
    setStep('processing');

    try {
      await createAccountTransferRequest(
        currentUserId,
        currentUserName,
        currentUserEmail,
        newOwnerEmail,
        companyName,
        companyKvK,
        transferReason,
        allEntityIds, // All entities are always transferred
        notes
      );

      alert('✅ Transfer aanvraag verzonden! De nieuwe eigenaar ontvangt een notificatie en moet de overdracht accepteren.');
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error creating transfer:', error);
      alert(`❌ Fout bij het aanmaken van de overdracht: ${error.message}`);
      setProcessing(false);
      setStep('review');
    }
  };

  if (step === 'confirm') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Account Overdragen</h2>
                <p className="text-sm text-gray-600">Dit is een onomkeerbare actie</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="p-6 bg-red-50 rounded-lg border-2 border-red-200">
              <h3 className="font-bold text-red-900 mb-3 flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                Waarschuwing: Permanente Actie
              </h3>
              <ul className="space-y-2 text-sm text-red-800">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Je verliest <strong>alle toegang</strong> tot dit account</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Alle managers, VA's en board members worden overgedragen</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Alle entities (netwerkclubs, communities, masterminds) worden overgedragen</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Doelstellingen, milestones en learning goals worden overgedragen</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>De nieuwe eigenaar krijgt volledige controle</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Deze actie kan <strong>niet</strong> ongedaan worden gemaakt</span>
                </li>
              </ul>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">✅ Complete Account Overdracht</h4>
              <p className="text-sm text-blue-800 mb-3">
                Het <strong>VOLLEDIGE account</strong> wordt overgedragen, inclusief:
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
                <div className="flex items-center">
                  <span className="mr-2">✓</span> Profiel & Instellingen
                </div>
                <div className="flex items-center">
                  <span className="mr-2">✓</span> Managers, VA's & Board
                </div>
                <div className="flex items-center">
                  <span className="mr-2">✓</span> Entities & Communities
                </div>
                <div className="flex items-center">
                  <span className="mr-2">✓</span> Doelstellingen & Goals
                </div>
                <div className="flex items-center">
                  <span className="mr-2">✓</span> Content & Posts
                </div>
                <div className="flex items-center">
                  <span className="mr-2">✓</span> Connecties & Netwerk
                </div>
                <div className="flex items-center">
                  <span className="mr-2">✓</span> Agenda & Calendar
                </div>
                <div className="flex items-center">
                  <span className="mr-2">✓</span> Time Tracking Data
                </div>
                <div className="flex items-center">
                  <span className="mr-2">✓</span> Chat Geschiedenis
                </div>
                <div className="flex items-center">
                  <span className="mr-2">✓</span> Analytics & Stats
                </div>
                <div className="flex items-center">
                  <span className="mr-2">✓</span> Notificaties & Inbox
                </div>
                <div className="flex items-center">
                  <span className="mr-2">✓</span> Expert Status
                </div>
              </div>
              <p className="text-xs text-blue-700 mt-3 italic">
                💡 Alles blijft zoals het was, alleen de naam wordt aangepast naar de nieuwe eigenaar
              </p>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Annuleren
              </button>
              <button
                onClick={() => setStep('details')}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center justify-center"
              >
                Ik begrijp het, doorgaan
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'details') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Overdracht Details</h2>
              <p className="text-sm text-gray-600">Vul de gegevens van de nieuwe eigenaar in</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Current Owner */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Huidige Eigenaar</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Bedrijf:</span>
                  <span className="ml-2 font-medium">{companyName}</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Email:</span>
                  <span className="ml-2 font-medium">{currentUserEmail}</span>
                </div>
              </div>
            </div>

            {/* New Owner */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                E-mailadres Nieuwe Eigenaar *
              </label>
              <input
                type="email"
                value={newOwnerEmail}
                onChange={(e) => setNewOwnerEmail(e.target.value)}
                placeholder="nieuwe.eigenaar@bedrijf.nl"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                De nieuwe eigenaar moet al een account hebben op SuperKonnected
              </p>
            </div>

            {/* Transfer Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reden voor Overdracht *
              </label>
              <select
                value={transferReason}
                onChange={(e) => setTransferReason(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecteer een reden...</option>
                <option value="Bedrijf verkocht">Bedrijf verkocht</option>
                <option value="Eigendomsoverdracht">Eigendomsoverdracht</option>
                <option value="Fusie">Fusie</option>
                <option value="Reorganisatie">Reorganisatie</option>
                <option value="Pensioen">Pensioen / Opvolging</option>
                <option value="Anders">Anders</option>
              </select>
            </div>

            {/* Entities - All will be transferred automatically */}
            {entities.length > 0 && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2 flex items-center">
                  <Building2 className="w-4 h-4 mr-2" />
                  Entities (worden allemaal overgedragen)
                </h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {entities.map((entity) => (
                    <div key={entity.id} className="flex items-center text-sm text-green-800">
                      <span className="mr-2">✓</span>
                      <span className="font-medium">{entity.name}</span>
                      <span className="text-xs text-green-600 ml-2">({entity.type})</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-green-700 mt-2">
                  📦 {entities.length} entit{entities.length === 1 ? 'eit' : 'eiten'} worden overgedragen
                </p>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opmerkingen (optioneel)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Eventuele aanvullende informatie..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Actions */}
            <div className="flex space-x-3 pt-4 border-t">
              <button
                onClick={() => setStep('confirm')}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Terug
              </button>
              <button
                onClick={() => setStep('review')}
                disabled={!newOwnerEmail || !transferReason}
                className="flex-1 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Volgende
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'review') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Controleer en Bevestig</h2>
              <p className="text-sm text-gray-600">Controleer alle details voor de overdracht</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="p-4 border border-gray-200 rounded-lg space-y-3">
              <div>
                <p className="text-sm text-gray-600">Van</p>
                <p className="font-medium">{currentUserEmail}</p>
              </div>
              <div className="flex items-center text-gray-400">
                <ArrowRight className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Naar</p>
                <p className="font-medium">{newOwnerEmail}</p>
              </div>
              <div className="pt-3 border-t">
                <p className="text-sm text-gray-600">Bedrijf</p>
                <p className="font-medium">{companyName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Reden</p>
                <p className="font-medium">{transferReason}</p>
              </div>
              {allEntityIds.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600">Wat wordt overgedragen</p>
                  <p className="font-medium">✅ VOLLEDIG account inclusief {allEntityIds.length} entit{allEntityIds.length === 1 ? 'eit' : 'eiten'}</p>
                </div>
              )}
            </div>

            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-amber-900">
                  Ik begrijp dat deze overdracht onomkeerbaar is en dat ik alle toegang tot dit account zal verliezen na acceptatie door de nieuwe eigenaar.
                </span>
              </label>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setStep('details')}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Terug
              </button>
              <button
                onClick={handleSubmit}
                disabled={!agreedToTerms || processing}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'Bezig met overdragen...' : 'Bevestig Overdracht'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Processing state
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-8 text-center max-w-md">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Overdracht aanmaken...</h3>
        <p className="text-gray-600">Even geduld, we verwerken je aanvraag.</p>
      </div>
    </div>
  );
};

export default AccountTransferModal;
